
import { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface CreditCardFormProps {
  amount: number;
  onSuccess: (paymentId: string) => void;
}

export const CreditCardForm = ({ amount, onSuccess }: CreditCardFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      // In a real implementation, you would call your backend to create a PaymentIntent
      // and return the client_secret
      const clientSecret = "test_client_secret"; // This would come from your server
      
      // For demo purposes, we're simulating a successful payment
      // In a real app, you'd use this:
      /*
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement)!,
        },
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.paymentIntent.status === 'succeeded') {
        onSuccess(result.paymentIntent.id);
      }
      */
      
      // Simulate successful payment for testing
      setTimeout(() => {
        const mockPaymentId = "pi_" + Math.random().toString(36).substr(2, 9);
        onSuccess(mockPaymentId);
        toast({
          title: "Payment successful",
          description: "Your payment was processed successfully",
        });
      }, 2000);
      
    } catch (error: any) {
      toast({
        title: "Payment failed",
        description: error.message || "An error occurred during payment processing",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="border border-gray-200 p-4 rounded-md bg-white">
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      <Button 
        type="submit" 
        disabled={!stripe || isProcessing}
        className="w-full bg-black text-white py-6 rounded-full text-xl hover:bg-black/90"
      >
        {isProcessing ? 'Processing...' : `Pay $${(amount / 100).toFixed(2)}`}
      </Button>
    </form>
  );
};
